import { useCallback, useSyncExternalStore } from 'react';

import { useRouter } from '@tanstack/react-router';

import { locationDocument, shellContextOf } from './documents';
import { admittedRouteFor, staticDataOf } from './navigation';
import { useSingleRoot } from './SingleRootContext';

import type { AnyRouter } from '@tanstack/react-router';

import type { SingleRootBootstrap } from './types';

/**
 * The Rails action of the location the shell shows: the admitted route's own, else the
 * server's document for that location (a probed gap), else the first served document's.
 * The route comes first: an admitted route reached client side has no server document.
 */
export const currentRailsAction = (
  router: AnyRouter | null,
  bootstrap: SingleRootBootstrap
): string => {
  if (router) {
    const routeAction = staticDataOf(
      admittedRouteFor(router, router.state.location.pathname)
    ).railsAction;
    if (routeAction) return routeAction;
    const documentAction = locationDocument(router)?.railsAction;
    if (documentAction) return documentAction;
  }
  return bootstrap.document.railsAction;
};

/**
 * The current location's Rails action for telemetry and gap tickets; null outside the shell.
 * Follows client navigations and probe answers.
 */
export const useCurrentRailsAction = (): string | null => {
  const shell = useSingleRoot();
  // Null outside a RouterProvider (a part rendered on its own): the bootstrap answers.
  const router: AnyRouter | null = useRouter<AnyRouter>({ warn: false }) ?? null;
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!router) return () => undefined;
      const unsubscribeRoute = router.subscribe('onResolved', onChange);
      const unsubscribeDocuments = shellContextOf(router)?.documents.subscribe(onChange);
      return () => {
        unsubscribeRoute();
        unsubscribeDocuments?.();
      };
    },
    [router]
  );
  return useSyncExternalStore(subscribe, () =>
    shell ? currentRailsAction(router, shell.bootstrap) : null
  );
};
